import React from 'react';
import styled from 'styled-components';
import { colors } from '../../../design-system';

interface Message {
  message_id: string;
  user_type: 'USER' | 'CREATOR';
  sender: string;
  subject?: string;
  received_at: string;
  content: string;
  has_attachments?: boolean;
  is_target?: boolean;
}

interface EmailThreadViewProps {
  messages: Message[];
  highlightText?: string;
  creatorProfileImage?: string;
  creatorEmails?: string[];
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 16px 24px;
  background: white;
`;

const ThreadContainer = styled.div`
  max-width: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MessageCard = styled.div<{ $isTarget?: boolean; $userType?: string }>`
  padding: 16px 0;
  position: relative;
  border-bottom: 1px solid ${colors.border.secondary};
  
  &:last-child {
    border-bottom: none;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  gap: 12px;
`;

const MessageAvatarContainer = styled.div`
  flex-shrink: 0;
  margin-top: 0;
  position: relative;
`;

const SquaredAvatar = styled.div<{ $userType: string; $hasImage?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  background: ${props => props.$hasImage ? 'transparent' : (props.$userType === 'CREATOR' ? colors.palette.purple[50] : colors.palette.blue[50])};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$userType === 'CREATOR' ? colors.palette.purple[600] : colors.palette.blue[600]};
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MessageContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const MessageHeader = styled.div`
  margin-bottom: 8px;
`;

const SenderSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0;
`;

const SenderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SenderName = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: ${colors.text.primary};
  margin-bottom: 1px;
  line-height: 1.4;
`;

const MessageTimestamp = styled.div`
  font-size: 10px;
  color: ${colors.text.tertiary};
  opacity: 0.7;
  flex-shrink: 0;
  margin-left: 12px;
  line-height: 1.4;
`;

const MessageSubject = styled.div`
  font-size: 10px;
  color: ${colors.text.tertiary};
  opacity: 0.7;
  line-height: 1.3;
  margin-top: 1px;
`;

const MessageBody = styled.div`
  color: ${colors.text.secondary};
  line-height: 1.5;
  font-size: 12px;
  word-wrap: break-word;
  white-space: pre-wrap;

  p {
    margin: 0 0 6px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const HighlightedText = styled.mark`
  background: rgba(251, 191, 36, 0.2);
  color: ${colors.text.primary};
  padding: 1px 0;
  border-radius: 2px;
  font-weight: 400;
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${colors.text.tertiary};
  font-size: 13px;
  padding: 40px 20px;
  opacity: 0.6;
`;

export const EmailThreadView: React.FC<EmailThreadViewProps> = ({
  messages,
  highlightText,
  creatorProfileImage,
  creatorEmails = []
}) => {
  // Helper to highlight text within content
  const highlightContent = (content: string, highlight?: string) => {
    if (!highlight || !content) return content;
    
    try {
      // Escape special regex characters
      const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const parts = content.split(new RegExp(`(${escapedHighlight})`, 'gi'));
      
      return parts.map((part, index) => 
        part.toLowerCase() === highlight.toLowerCase() 
          ? <HighlightedText key={index}>{part}</HighlightedText>
          : part
      );
    } catch (e) {
      return content;
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Get initials for avatar
  const getInitials = (email: string, userType: string) => {
    if (userType === 'CREATOR') {
      return 'C';
    }
    return 'U';
  };

  // Check if sender is creator (matches any creator email)
  const isCreatorEmail = (senderEmail: string) => {
    const normalizedSender = senderEmail.toLowerCase().trim();
    return creatorEmails.some(email => email.toLowerCase().trim() === normalizedSender);
  };

  if (!messages || messages.length === 0) {
    return (
      <Container>
        <EmptyState>No messages available</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <ThreadContainer>
        {messages.map((message) => (
          <MessageCard
            key={message.message_id}
            $isTarget={message.is_target}
            $userType={message.user_type}
          >
            <MessageWrapper>
              <MessageAvatarContainer>
                <SquaredAvatar 
                  $userType={message.user_type}
                  $hasImage={message.user_type === 'CREATOR' && !!creatorProfileImage && isCreatorEmail(message.sender)}
                >
                  {message.user_type === 'CREATOR' && creatorProfileImage && isCreatorEmail(message.sender) ? (
                    <AvatarImage 
                      src={creatorProfileImage} 
                      alt={message.sender}
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    getInitials(message.sender, message.user_type)
                  )}
                </SquaredAvatar>
              </MessageAvatarContainer>
              
              <MessageContentWrapper>
                <MessageHeader>
                  <SenderSection>
                    <SenderInfo>
                      <SenderName>{message.sender}</SenderName>
                      {message.subject && (
                        <MessageSubject>{message.subject}</MessageSubject>
                      )}
                    </SenderInfo>
                    <MessageTimestamp>
                      {formatDate(message.received_at)}
                    </MessageTimestamp>
                  </SenderSection>
                </MessageHeader>
                
                <MessageBody>
                  {highlightContent(message.content, highlightText)}
                </MessageBody>
              </MessageContentWrapper>
            </MessageWrapper>
          </MessageCard>
        ))}
      </ThreadContainer>
    </Container>
  );
};

